import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Templates pré-remplis (modifiables par l'utilisateur avant envoi)
function buildSMSTemplate({ prenom, nom, note, type }) {
  const nomComplet = [prenom, nom].filter(Boolean).join(' ').trim() || 'cher client';
  if (note >= 4) {
    return `Bonjour ${nomComplet}, merci pour votre avis ${note}/5 ! Nous restons a votre disposition.`;
  }
  if (note <= 3) {
    return `Bonjour ${nomComplet}, nous avons bien recu votre avis ${note}/5. Pouvez-vous nous rappeler pour en discuter ? Merci.`;
  }
  return `Bonjour ${nomComplet}, merci d avoir pris le temps de repondre a notre enquete.`;
}

function buildEmailTemplate({ prenom, nom, note, commentaire, immat, marque, modele, numero_or, type }) {
  const nomComplet = [prenom, nom].filter(Boolean).join(' ').trim() || 'cher client';
  const subject = note >= 4
    ? `Merci pour votre avis ${note}/5 !`
    : (note <= 3
      ? `Suite à votre avis sur votre passage en concession`
      : `Suite à votre retour sur votre passage en concession`);

  let body = `Bonjour ${nomComplet},\n\n`;
  if (note >= 4) {
    body += `Nous tenons à vous remercier pour le temps que vous avez consacré à répondre à notre enquête de satisfaction et pour la note de ${note}/5 que vous nous avez attribuée.\n\n`;
  } else if (note <= 3) {
    body += `Nous avons pris connaissance de votre avis (${note}/5) suite à votre récent passage`;
    if (type === 'apv') body += ` à l'atelier`;
    if (numero_or) body += ` (OR n°${numero_or})`;
    body += `. Nous sommes désolés que votre expérience n'ait pas été à la hauteur de vos attentes.\n\n`;
    body += `Pourriez-vous nous accorder quelques minutes par téléphone afin que nous puissions échanger sur les points à améliorer ?\n\n`;
  } else {
    body += `Nous vous remercions d'avoir répondu à notre enquête de satisfaction.\n\n`;
  }
  if (commentaire) {
    body += `Vous nous avez écrit :\n« ${commentaire} »\n\n`;
  }
  body += `Restant à votre disposition,\nL'équipe de votre concession`;

  return { subject, body };
}

function JDMAReplyModal({ open, onClose, avis, type, onSent }) {
  const [canal, setCanal] = useState('sms'); // 'sms' | 'email'
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const [smsMessage, setSmsMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  useEffect(() => {
    if (!open || !avis) return;
    const data = avis.data || {};
    const ctx = {
      prenom: data['Prénom Feedbacker'],
      nom: data['Nom Feedbacker'],
      note: avis.note,
      commentaire: avis.commentaire,
      immat: data['immatriculation'],
      marque: data['marque'],
      modele: data['modele'],
      numero_or: data['numero or'],
      type,
    };
    setSmsMessage(buildSMSTemplate(ctx));
    const tpl = buildEmailTemplate(ctx);
    setEmailSubject(tpl.subject);
    setEmailBody(tpl.body);
    setError(null);
    // Sélectionner par défaut le canal disponible
    if (avis.telephone_client) setCanal('sms');
    else if (avis.email_client) setCanal('email');
  }, [open, avis, type]);

  if (!open || !avis) return null;

  const hasSms = !!avis.telephone_client;
  const hasEmail = !!avis.email_client;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      if (canal === 'sms') {
        await api.post(`/jdma/${type}/${avis.id}/sms`, { message: smsMessage });
      } else {
        await api.post(`/jdma/${type}/${avis.id}/email`, {
          subject: emailSubject,
          text: emailBody,
        });
      }
      if (onSent) onSent();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const data = avis.data || {};
  const nomClient = [data['Prénom Feedbacker'], data['Nom Feedbacker']].filter(Boolean).join(' ');

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Répondre au client</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="jdma-reply-context">
            <strong>{nomClient || 'Client anonyme'}</strong>
            {avis.note != null && <> — Note : <strong>{avis.note}/5</strong></>}
            {data['immatriculation'] && <> — {data['immatriculation']}</>}
            {data['numero or'] && <> — OR {data['numero or']}</>}
          </div>
          {avis.commentaire && (
            <blockquote className="jdma-reply-comment">« {avis.commentaire} »</blockquote>
          )}

          <div className="jdma-reply-canal">
            <button
              className={`jdma-reply-canal-btn ${canal === 'sms' ? 'active' : ''}`}
              onClick={() => setCanal('sms')}
              disabled={!hasSms}
              title={hasSms ? '' : 'Aucun numéro de téléphone'}
            >
              📱 SMS{!hasSms && ' (indisponible)'}
            </button>
            <button
              className={`jdma-reply-canal-btn ${canal === 'email' ? 'active' : ''}`}
              onClick={() => setCanal('email')}
              disabled={!hasEmail}
              title={hasEmail ? '' : 'Aucun email'}
            >
              📧 Email{!hasEmail && ' (indisponible)'}
            </button>
          </div>

          {canal === 'sms' && (
            <div className="jdma-reply-form">
              <label>Destinataire</label>
              <input type="text" value={avis.telephone_client || ''} disabled />
              <label>Message <span className="jdma-reply-counter">{smsMessage.length}/160</span></label>
              <textarea
                rows={5}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                maxLength={160}
              />
            </div>
          )}

          {canal === 'email' && (
            <div className="jdma-reply-form">
              <label>Destinataire</label>
              <input type="text" value={avis.email_client || ''} disabled />
              <label>Sujet</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
              <label>Message</label>
              <textarea
                rows={10}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>
          )}

          {error && <div className="jdma-reply-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={sending}>Annuler</button>
          <button
            className="btn-primary"
            onClick={handleSend}
            disabled={sending || (canal === 'sms' && !hasSms) || (canal === 'email' && !hasEmail)}
          >
            {sending ? 'Envoi…' : (canal === 'sms' ? 'Envoyer le SMS' : 'Envoyer l\'email')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default JDMAReplyModal;
